import { computed, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpContext } from '@angular/common/http';
import { environment } from '@admin/env/environment';
import { firstValueFrom } from 'rxjs';
import { AccessTokenResponse } from '../interfaces/auth-token-response.interface';
import { TokenService } from './token.service';
import { UserDetails } from '@admin/auth/interfaces/user-details.interface';
import { SUPPRESS_ERROR_TOAST } from '@admin/shared/http/suppress-error-toast.token';

const AUTH_API = `${environment.BASE_URL}/api`;

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    constructor(
        private router: Router,
        private http: HttpClient,
        private tokenService: TokenService,
    ) {}

    public email = signal<string | null>(null);
    public hasRequestedOtp = signal<boolean>(false);
    public currentUser = signal<UserDetails | null>(null);

    public isAuthenticated = computed(() => !!this.tokenService.getAccessToken());

    public async requestOtpCode(emailAddress: string): Promise<void> {
        const body = {
            email: emailAddress,
        };
        const context = new HttpContext().set(SUPPRESS_ERROR_TOAST, true);

        await firstValueFrom(this.http.post(`${AUTH_API}/otp`, body, { context }));

        this.hasRequestedOtp.set(true);
    }

    public async validateOtpCode(code: string): Promise<AccessTokenResponse> {
        const body = {
            email: this.email(),
            code: code,
        };
        const context = new HttpContext().set(SUPPRESS_ERROR_TOAST, true);

        return await firstValueFrom(this.http.post<AccessTokenResponse>(`${AUTH_API}/otp/verify`, body, { context }));
    }

    public async getLoggedInUserDetails(): Promise<UserDetails> {
        return await firstValueFrom(this.http.get<UserDetails>(`${AUTH_API}/users/me`));
    }

    public async login(accessToken: string, refreshToken: string): Promise<void> {
        this.tokenService.setTokens(accessToken, refreshToken);

        await this.router.navigate(['/work-packages']);
    }

    public async logout(): Promise<void> {
        this.tokenService.clear();

        await this.router.navigate(['/auth']);
    }
}
